import { Link } from 'react-router-dom'

import {
  formatTimeZone,
  GlobeIcon,
  Tooltip,
  Button,
} from '@buffer-mono/popcorn'
import type { ButtonProps } from '@buffer-mono/popcorn'

import { useCompatibleTimezone } from '~publish/components/TimeZoneSelect/hooks'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import * as routes from '~publish/legacy/routes'

import type { JSX } from 'react'

const getShortenedTimezone = (timezone: string): string => {
  const timezoneParts = timezone.split('/')
  return timezoneParts[timezoneParts.length - 1]
}

type TimezoneDisplayProps = Omit<ButtonProps, 'children'> & {
  channelId?: string
  timezone?: string
}

export const TimezoneDisplay = ({
  channelId,
  disabled,
  timezone,
  ...buttonProps
}: TimezoneDisplayProps): JSX.Element => {
  const compatibleTimezone = useCompatibleTimezone(timezone)
  const displayTimezone = formatTimeZone(
    compatibleTimezone?.name ?? CURRENT_TIME_ZONE,
  )

  const content = (
    <>
      <GlobeIcon aria-hidden={false} aria-label="Time zone" />
      {getShortenedTimezone(displayTimezone)}
    </>
  )

  // Anchors ignore the `disabled` attribute — a disabled Link would still
  // navigate — so the disabled state renders a plain disabled button instead.
  if (channelId && !disabled) {
    return (
      <Tooltip content={`Channel time zone: ${displayTimezone}`}>
        <Button
          variant="tertiary"
          as={Link}
          to={routes.channelSettings.getRoute({
            channelId,
            tab: 'posting-schedule',
          })}
          {...buttonProps}
        >
          {content}
        </Button>
      </Tooltip>
    )
  }
  return (
    <Tooltip
      content={
        channelId
          ? `Channel time zone: ${displayTimezone}`
          : `Current time zone: ${displayTimezone}`
      }
    >
      <Button
        variant="tertiary"
        disabled={disabled || !buttonProps.onClick}
        {...buttonProps}
      >
        {content}
      </Button>
    </Tooltip>
  )
}
